import React, { useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { CalendarEvent } from '@factorialco/f0-react/dist/experimental'

import { observer } from 'mobx-react'

import LearnMoreAboutLink from 'modules/core/components/LearnMoreAboutLink'
import i18n from 'modules/core/lib/i18n'

interface Props {
  title: string
  description: string
  fromDate?: Date
  toDate: Date
  color?: string
}

const AbsenceSummaryContent = ({ title, description, fromDate, toDate, color }: Props) => {
  const iconRef = useRef<HTMLSpanElement>(null)
  const [showTooltip, setShowTooltip] = useState(false)
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const [isTooltipHovered, setIsTooltipHovered] = useState(false)

  const tooltipLabel = () => {
    return (
      <LearnMoreAboutLink
        ledeText={i18n.t('time_off.absence_summary.tooltip.title')}
        helpCenterKey="timeoff_calculations"
        linkText={i18n.t('time_off.absence_summary.tooltip.link')}
        color="grey000White"
      />
    )
  }

  const handleMouseEnter = () => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect()
      setCoords({
        top: rect.bottom + window.scrollY + 6, // 6px offset
        left: rect.left + window.scrollX + rect.width / 2,
      })
      setShowTooltip(true)
    }
  }

  const handleMouseLeave = () => {
    setTimeout(() => {
      if (!isTooltipHovered) setShowTooltip(false)
    }, 50)
  }

  const handleTooltipEnter = () => {
    setIsTooltipHovered(true)
    setShowTooltip(true)
  }

  const handleTooltipLeave = () => {
    setIsTooltipHovered(false)
    setShowTooltip(false)
  }

  return (
    <Box gap="s2">
      <Box flexDirection="row" alignItems="center" gap="s4">
        <Text.Normal size="200" weight="medium" color="grey1000">
          {i18n.t('time_off.absence_summary.label')}
        </Text.Normal>
        <span
          ref={iconRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ display: 'inline-flex', cursor: 'pointer' }}
        >
          <Icon.Medium icon="info-circle" color="grey800" />
        </span>
        {showTooltip &&
          createPortal(
            <Box
              position="absolute"
              style={{
                top: `${coords.top}px`,
                left: `${coords.left}px`,
                transform: 'translateX(-50%)',
                zIndex: '9999',
                minWidth: '220px',
                maxWidth: '320px',
              }}
              background="grey1000"
              padding="s12"
              borderRadius={{ all: 'abs008' }}
              onMouseEnter={handleTooltipEnter}
              onMouseLeave={handleTooltipLeave}
            >
              <Text.Normal color="grey000White">{tooltipLabel()}</Text.Normal>
            </Box>,
            document.body
          )}
      </Box>
      <CalendarEvent
        color={color === 'group1' ? '#07A2AD' : 'grey'}
        description={description}
        fromDate={fromDate}
        isPending={false}
        title={title}
        toDate={toDate}
      />
    </Box>
  )
}

export default observer(AbsenceSummaryContent)
